import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  UiIcon,
  UiDialogTitle,
  UiDialogContent,
  UiDialogActions,
  UiButton,
  UiTextField,
  UiLabel,
} from 'ui-components';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useApi } from 'hooks';
import { setCurrentQuery, setQueries, applyFiltersToApiRequest } from 'store';
import { toast } from 'lib';
import { StyledSaveSubjectsDialog } from './SaveSubjectsModal.styles';

const buttonTheme = createTheme({
  direction: 'rtl',
  palette: {
    mode: 'light',
    primary: {
      main: '#199DA3',
      fontFamily: 'inherit !important',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'inherit !important',
          fontSize: '0.75rem !important',
          color: '#333 !important',
          backgroundColor: '#FFF !important',
          padding: '0 !important',
          minWidth: 36,

          '& .icon': {
            color: 'rgba(25, 157, 163, 0.7)',
            transition: 'color 0.25s ease',
          },

          '&:hover': {
            '& .icon': {
              color: '#199DA3',
            },
          },
        },
      },
    },
  },
});

const SaveSubjectsModal = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentQuery = useSelector((state) => state.pageReducer.currentQuery);
  const queries = useSelector((state) => state.pageReducer.queries);
  const { add, loadingAdd, getApiParams } = useApi();
  const [value, setValue] = useState('');
  const [visibleModal, setVisibleModal] = useState(false);

  const open = () => setVisibleModal(true);

  const close = () => setVisibleModal(false);

  const handleSubmit = () => {
    const { path, dataModel } = getApiParams('addQuery');
    add(
      path,
      {
        title: value,
        description: '',
        parsed_query: currentQuery?.q,
        // simple_query: null
      },
      dataModel
    )
      .then((result) => {
        toast({
          title: t('common:successOperation'),
          description: t('common:changedSuccessfully'),
          type: 'success',
        });

        dispatch(setCurrentQuery({ queryId: result.data.id, q: undefined }));
        dispatch(
          setQueries([
            ...queries.data,
            {
              id: result.data.id,
              parsed_query: currentQuery?.q,
              title: value,
            },
          ])
        );
        dispatch(applyFiltersToApiRequest());

        setVisibleModal(false);
        setValue('');
      })
      .catch((err) => {
        if (typeof err?.response?.data?.errors?.data?.message === 'string')
          toast({
            title: t('common:errorInSave'),
            description: err?.response?.data?.errors?.data?.message,
            type: 'error',
          });
        else
          toast({
            title: t('common:errorInSave'),
            description: t('content:error.saveSubject'),
            type: 'error',
          });
      });
  };

  return (
    <div className='save-subjects-modal'>
      <ThemeProvider theme={buttonTheme}>
        <UiButton
          variant='outlined'
          onClick={open}
          className='subjects-button w-48 h-48'
        >
          <span className='flex'>
            <UiIcon icon='ri-save-3-line' size={24} />
          </span>
        </UiButton>
      </ThemeProvider>
      <StyledSaveSubjectsDialog
        open={visibleModal}
        onClose={close}
        className='save-subjects-modal'
      >
        {currentQuery.q && (
          <UiDialogTitle className='header'>
            <div>{t('content:saveAsSubject')}</div>
            <div onClick={close} className='flex'>
              <UiIcon icon='ri-close-line' color='#454545' />
            </div>
          </UiDialogTitle>
        )}

        {currentQuery.q ? (
          <UiDialogContent>
            <UiLabel className='label'>{t('subject:subjectName')}</UiLabel>
            <div className='mt-10'>
              <UiTextField
                placeholder={t('content:inputSubjectName')}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={{
                  width: 250,
                }}
              ></UiTextField>
            </div>
          </UiDialogContent>
        ) : (
          <UiDialogContent>
            <div className='mt-10 bold text-center'>
              {t('content:thisSubjectAddedBefore')}
            </div>
          </UiDialogContent>
        )}
        <UiDialogActions
          className={`actions ${currentQuery.q ? '' : 'flex center'}`}
        >
          {currentQuery.q ? (
            <>
              <UiButton variant='text' onClick={close} color='primary'>
                {t('common:cancel')}
              </UiButton>
              <UiButton
                variant='contained'
                onClick={handleSubmit}
                loading={loadingAdd}
                disabled={value.length > 2 ? loadingAdd : true}
              >
                {t('common:save')}
              </UiButton>
            </>
          ) : (
            <UiButton variant='outlined' onClick={close}>
              {t('common:IRealized')}
            </UiButton>
          )}
        </UiDialogActions>
      </StyledSaveSubjectsDialog>
    </div>
  );
};

export default SaveSubjectsModal;
